import { useEffect, useMemo, useRef, useState } from 'react';
import { Gamepad2, LayoutGrid, Mic, MicOff, Paperclip, Phone, PhoneCall, Sparkles } from 'lucide-react';

import { Button } from '@/OS/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/blueprints/ui/OSAwareDialog';
import { Input } from '@/OS/components/ui/input';
import { ScrollArea } from '@/OS/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { MessengerInviteCategory, MessengerInviteOption } from './actionPayload';
import { getInviteOptions } from './actionPayload';

interface MessengerActionToolbarProps {
  onOpenFiles: () => void;
  onVoiceAction: () => void;
  onVoiceToggleMute: () => void;
  onSendInvite: (option: MessengerInviteOption, category: MessengerInviteCategory, note?: string) => void | Promise<void>;
  voicePhase: 'idle' | 'outgoing' | 'incoming' | 'connecting' | 'active';
  voiceMuted: boolean;
}

const DIALOG_COPY: Record<MessengerInviteCategory, { title: string; description: string }> = {
  invite: {
    title: 'Share a MapleOS app',
    description: 'Pick an app to open from this conversation.',
  },
  activity: {
    title: 'Share an activity',
    description: 'Send a quick MapleOS activity from this chat.',
  },
  game: {
    title: 'Share a game',
    description: 'Launch a game link straight from Messenger.',
  },
};

const COMPACT_TOOLBAR_WIDTH = 760;

export default function MessengerActionToolbar({
  onOpenFiles,
  onVoiceAction,
  onVoiceToggleMute,
  onSendInvite,
  voicePhase,
  voiceMuted,
}: MessengerActionToolbarProps) {
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [compact, setCompact] = useState(false);
  const [dialogMode, setDialogMode] = useState<MessengerInviteCategory | null>(null);
  const [selectedAppId, setSelectedAppId] = useState<string>('');
  const [note, setNote] = useState('');

  useEffect(() => {
    const element = toolbarRef.current;

    if (!element) {
      return;
    }

    const updateCompact = () => {
      setCompact(element.clientWidth < COMPACT_TOOLBAR_WIDTH);
    };

    updateCompact();

    const observer = new ResizeObserver(updateCompact);
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const inviteOptions = useMemo(() => {
    if (!dialogMode) {
      return [];
    }

    return getInviteOptions(dialogMode);
  }, [dialogMode]);

  const selectedOption = inviteOptions.find((option) => option.appId === selectedAppId) ?? inviteOptions[0];

  const openDialog = (mode: MessengerInviteCategory) => {
    const options = getInviteOptions(mode);
    setDialogMode(mode);
    setSelectedAppId(options[0]?.appId || '');
    setNote('');
  };

  const handleSendInvite = async () => {
    if (!dialogMode || !selectedOption) {
      return;
    }

    await onSendInvite(selectedOption, dialogMode, note.trim());
    setDialogMode(null);
    setNote('');
  };

  const voiceLabel =
    voicePhase === 'active'
      ? 'In call'
      : voicePhase === 'incoming'
        ? 'Incoming call'
        : voicePhase === 'outgoing' || voicePhase === 'connecting'
          ? 'Calling…'
          : 'Voice';

  const actionButtonClassName = cn(
    compact
      ? 'h-9 w-9 border border-border/60 bg-background/80 px-0 text-foreground shadow-sm hover:bg-accent hover:text-accent-foreground'
      : 'gap-2 text-foreground hover:bg-accent hover:text-accent-foreground'
  );

  const muteButtonClassName = cn(
    'border-border bg-background text-foreground hover:bg-accent hover:text-accent-foreground',
    compact ? 'h-9 w-9 px-0 shadow-sm' : 'gap-2'
  );

  return (
    <>
      <div className="border-b border-border/70 bg-muted/30 px-4 py-2">
        <div ref={toolbarRef} className="flex flex-nowrap items-center gap-2 overflow-x-auto">
          <Button
            variant="ghost"
            size="sm"
            className={actionButtonClassName}
            onClick={() => openDialog('invite')}
            aria-label="Apps"
            title="Apps"
          >
            <LayoutGrid className="h-4 w-4 shrink-0" />
            {!compact && <span>Apps</span>}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={actionButtonClassName}
            onClick={onOpenFiles}
            aria-label="Send Files"
            title="Send Files"
          >
            <Paperclip className="h-4 w-4 shrink-0" />
            {!compact && <span>Send Files</span>}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={actionButtonClassName}
            onClick={onVoiceAction}
            aria-label={voiceLabel}
            title={voiceLabel}
          >
            {voicePhase === 'active' ? (
              <PhoneCall className="h-4 w-4 shrink-0" />
            ) : (
              <Phone className="h-4 w-4 shrink-0" />
            )}
            {!compact && <span>{voiceLabel}</span>}
          </Button>
          {voicePhase === 'active' && (
            <Button
              variant="outline"
              size="sm"
              className={muteButtonClassName}
              onClick={onVoiceToggleMute}
              aria-label={voiceMuted ? 'Unmute' : 'Mute'}
              title={voiceMuted ? 'Unmute' : 'Mute'}
            >
              {voiceMuted ? <MicOff className="h-4 w-4 shrink-0" /> : <Mic className="h-4 w-4 shrink-0" />}
              {!compact && <span>{voiceMuted ? 'Unmute' : 'Mute'}</span>}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className={actionButtonClassName}
            onClick={() => openDialog('activity')}
            aria-label="Activities"
            title="Activities"
          >
            <Sparkles className="h-4 w-4 shrink-0" />
            {!compact && <span>Activities</span>}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={actionButtonClassName}
            onClick={() => openDialog('game')}
            aria-label="Games"
            title="Games"
          >
            <Gamepad2 className="h-4 w-4 shrink-0" />
            {!compact && <span>Games</span>}
          </Button>
          {voicePhase !== 'idle' && !compact && <Badge variant="secondary">{voiceLabel}</Badge>}
        </div>
      </div>

      <Dialog embedded open={dialogMode !== null} onOpenChange={(open) => !open && setDialogMode(null)}>
        <DialogContent embedded className="max-w-xl p-0">
          <div className="border-b border-border/70 bg-muted/30 px-6 py-5">
            <DialogHeader embedded className="space-y-2 text-left">
              <DialogTitle embedded className="text-2xl font-semibold text-foreground">
                {dialogMode ? DIALOG_COPY[dialogMode].title : 'Share'}
              </DialogTitle>
              <DialogDescription embedded className="text-base text-muted-foreground">
                {dialogMode ? DIALOG_COPY[dialogMode].description : ''}
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="space-y-5 px-6 py-5">
            <ScrollArea className="max-h-72 pr-3">
              <div className="space-y-3">
                {inviteOptions.map((option) => {
                  const OptionIcon = option.icon;
                  return (
                    <button
                      key={option.appId}
                      type="button"
                      onClick={() => setSelectedAppId(option.appId)}
                      className={cn(
                        'flex w-full items-start gap-3 rounded-md border border-border/70 bg-card/70 p-4 text-left transition-colors hover:border-primary/40 hover:bg-accent/30 focus:outline-none focus:ring-2 focus:ring-ring',
                        selectedAppId === option.appId && 'border-primary bg-accent/60 shadow-sm'
                      )}
                    >
                      <div className="rounded-md bg-primary/10 p-2 text-primary">
                        <OptionIcon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-foreground">{option.title}</span>
                          <Badge
                            variant="outline"
                            className="rounded-full border-border/70 bg-muted/50 px-2.5 py-0.5 capitalize text-[11px] text-muted-foreground"
                          >
                            {option.category}
                          </Badge>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">{option.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </ScrollArea>

            <div className="space-y-2">
              <label htmlFor="invite-note" className="text-xs font-medium text-muted-foreground">
                Optional note
              </label>
              <Input
                id="invite-note"
                value={note}
                onChange={(event) => setNote(event.target.value.slice(0, 120))}
                placeholder="Check this out in MapleOS"
                maxLength={120}
              />
            </div>
          </div>

          <DialogFooter embedded className="border-t border-border/70 bg-muted/20 px-6 py-4 sm:justify-end sm:space-x-2">
            <Button variant="ghost" onClick={() => setDialogMode(null)}>
              Cancel
            </Button>
            <Button onClick={handleSendInvite} disabled={!selectedOption}>
              Share
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}